import { useTranslation } from '../../context/LanguageContext';
import { formatNumber } from '../../utils/format';

// Re-exported so the admin components' existing imports keep working; the single
// definition now lives in utils/format and is shared with the dashboard.
export { formatDate, formatDateTime, formatNumber } from '../../utils/format';

export function AdminPanel({
  title,
  actions,
  children,
}: Readonly<{
  title: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
}>) {
  return (
    <div className="bg-slate-800 rounded-xl border border-slate-700 shadow-sm overflow-hidden">
      <div className="flex flex-wrap items-center gap-3 px-5 py-4 border-b border-slate-700">
        <h3 className="text-sm font-bold text-white">{title}</h3>
        <div className="ml-auto flex items-center gap-2">{actions}</div>
      </div>
      {children}
    </div>
  );
}

export function AdminStateRow({
  loading,
  error,
  empty,
  emptyLabel,
}: Readonly<{
  loading: boolean;
  error: string | null;
  empty: boolean;
  emptyLabel: string;
}>) {
  const { t } = useTranslation();
  if (loading) {
    return <p className="px-5 py-10 text-center text-xs text-slate-400">{t('common.loading')}</p>;
  }
  if (error) {
    return <p className="px-5 py-10 text-center text-xs text-rose-400">{error}</p>;
  }
  if (empty) {
    return <p className="px-5 py-10 text-center text-xs text-slate-500">{emptyLabel}</p>;
  }
  return null;
}

export function Pager({
  page,
  pageSize,
  total,
  onChange,
}: Readonly<{
  page: number;
  pageSize: number;
  total: number;
  onChange: (next: number) => void;
}>) {
  const { t } = useTranslation();
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  if (total === 0) return null;

  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  return (
    <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-slate-700">
      <span className="text-xs text-slate-500">{t('admin.showing', { from, to, total })}</span>
      <div className="flex items-center gap-1">
        <button
          type="button"
          disabled={page === 1}
          onClick={() => onChange(page - 1)}
          className="px-3 py-1.5 rounded-lg border border-slate-700 text-xs text-slate-400 hover:bg-slate-700 disabled:opacity-40 disabled:hover:bg-transparent transition-colors"
        >
          {t('common.prev')}
        </button>
        <span className="px-2 text-xs text-slate-400">
          {page}/{totalPages}
        </span>
        <button
          type="button"
          disabled={page >= totalPages}
          onClick={() => onChange(page + 1)}
          className="px-3 py-1.5 rounded-lg border border-slate-700 text-xs text-slate-400 hover:bg-slate-700 disabled:opacity-40 disabled:hover:bg-transparent transition-colors"
        >
          {t('common.next')}
        </button>
      </div>
    </div>
  );
}

/**
 * Horizontal proportion bar, matching the subscription usage bar's visual
 * language. Used instead of a charting library — there is none in the project
 * and two bar lists do not justify adding one.
 */
export function BarRow({
  label,
  value,
  max,
  hint,
}: Readonly<{ label: string; value: number; max: number; hint?: string }>) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="px-5 py-2.5">
      <div className="flex items-baseline justify-between gap-2 mb-1.5">
        <span className="text-xs font-medium text-slate-300 truncate">{label}</span>
        <span className="text-xs font-bold text-violet-400 flex-shrink-0">
          {formatNumber(value)}
          {hint ? <span className="text-slate-500 font-normal"> {hint}</span> : null}
        </span>
      </div>
      <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full bg-violet-500 transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}
